import type { ReactNode } from "react";
import { useParlorMultiSegment } from "@/hooks/useParlorMultiSegment";

type Props = {
  /** distinct 식장 ≥ 2 — 여기 안의 useCustomVariation 만 multi 모수에 들어감 */
  multi?: ReactNode;
  /** multi 가 아닌 유저 (첫 방문 · 동일 식장 재방문 포함) */
  nonMulti?: ReactNode;
  /**
   * 세그먼트 판정 전. 기본 null.
   * 로딩 중 multi/nonMulti 를 미리 마운트하면 Hackle 오할당이 난다 (#2494 교훈).
   */
  fallback?: ReactNode;
};

/**
 * multi / non_multi 실험 모수 분리 게이트 (페이지 무관).
 *
 * 영민님 TimeSplitContainer(#2494) 와 같은 철학:
 * **렌더되지 않은 쪽은 마운트되지 않으므로 그 안의 실험도 variation 을 호출하지 않는다.**
 *
 * 방문 기록(write)은 이 컴포넌트 밖 책임이다.
 * - 식장 상세: 게이트/페이지에서 `upsertCompareVisit` 후 이 Split 사용
 * - 홈·매칭·로그인 등: 이미 쌓인 이력만 읽고 multi/non_multi 로 실험 분기
 *
 * @example 홈 등 비-식장 페이지
 * ```tsx
 * <ParlorMultiSplitContainer
 *   multi={<ABEnabledContainer experimentKey={AAA} ... />}
 *   nonMulti={<ABEnabledContainer experimentKey={BBB} ... />}
 * />
 * ```
 *
 * @example 식장 상세 (방문 기록은 호출부가 먼저)
 * ```tsx
 * upsertCompareVisit({ id, name, region });
 * <ParlorMultiSplitContainer multi={<SomeMultiOnlyExperiment />} />
 * ```
 *
 * QA: `?force_parlor_multi=1` | `0` | `multi` | `non_multi`
 */
export function ParlorMultiSplitContainer({
  multi = null,
  nonMulti = null,
  fallback = null,
}: Props) {
  const { segment } = useParlorMultiSegment();

  if (segment === "loading") return <>{fallback}</>;
  if (segment === "multi") return <>{multi}</>;
  return <>{nonMulti}</>;
}

export default ParlorMultiSplitContainer;
